import React, { useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	TextField,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { updatePhoneFromAppcenter, useAuth } from '../redux/slices/auth';
import { I18NKey, useI18N } from '../i18n';
import { NewTicketData, updateTicket } from '../redux/slices/tickets';
import { useRedux } from '../redux';

type Errors = {
	phoneArea: I18NKey;
	phone: I18NKey;
};

interface UpdatePhoneModalProps {
	open: boolean;
	onClose: () => void;
	onSkip?: () => void;
	onContinue?: () => void;
	ticket?: NewTicketData;
}

const UpdatePhoneModal: React.FC<UpdatePhoneModalProps> = ({
	open,
	onClose,
	onSkip,
	onContinue,
	ticket,
}) => {
	const i18n = useI18N();
	const dispatch = useDispatch();
	const { snackbar } = useRedux();
	const { user } = useAuth();
	const [phoneArea, setPhoneArea] = useState(
		ticket ? ticket.phone.slice(0, 2) : user?.phoneArea || ''
	);
	const [phone, setPhone] = useState(ticket ? ticket.phone.slice(2, 11) : user?.phone || '');
	const [errors, setErrors] = useState<Errors>({
		phoneArea: '',
		phone: '',
	});

	const handleAddPhone = async () => {
		const validationErrors: Errors = { phoneArea: '', phone: '' };
		let hasError = false;

		if (phoneArea.length !== 2) {
			validationErrors.phoneArea = 'dialogs.updatePhone.errors.phoneArea.invalid';
			hasError = true;
		}

		if (phone.length !== 9) {
			validationErrors.phone = 'dialogs.updatePhone.errors.phone.invalid';
			hasError = true;
		}

		if (hasError) {
			setErrors(validationErrors);
			return;
		}

		if (ticket) {
			try {
				dispatch(
					updateTicket({
						ticketId: ticket.ticketId || '',
						updateFields: {
							phone: phoneArea + phone,
						},
					})
				);
				snackbar.show({ status: 'success', message: 'tickets.list.whatsappPhone' });
			} catch (error) {
				snackbar.show({ status: 'error', message: 'tickets.list.whatsappPhone' });
			}
		} else {
			dispatch(updatePhoneFromAppcenter({ uid: user?.uid || '', phoneArea, phone }));
		}
		onContinue && onContinue();
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
			<DialogTitle>Receber notificações por WhatsApp</DialogTitle>

			<DialogContent>
				<Typography variant="body1">
					Para receber notificações sobre o ticket, você precisa cadastrar seu número de telefone.
				</Typography>

				<TextField
					placeholder="Insira o DDD. Ex: 11"
					variant="outlined"
					type="number"
					value={phoneArea}
					onChange={(e) => setPhoneArea(e.target.value)}
					fullWidth
					style={{ margin: '1rem 0' }}
					error={!!errors.phoneArea}
					helperText={i18n(errors.phoneArea)}
				/>

				<TextField
					placeholder="Insira o telefone. Ex: 999999999"
					type="number"
					variant="outlined"
					value={phone}
					onChange={(e) => setPhone(e.target.value)}
					fullWidth
					style={{ margin: '1rem 0' }}
					error={!!errors.phone}
					helperText={i18n(errors.phone)}
				/>
			</DialogContent>

			<DialogActions>
				<Button onClick={handleAddPhone} variant="contained" color="primary">
					Salvar e continuar
				</Button>
				{onSkip ? (
					<Button onClick={onSkip} variant="outlined" color="secondary">
						Continuar sem número
					</Button>
				) : (
					<Button onClick={onClose} variant="outlined" color="secondary">
						Cancelar
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default UpdatePhoneModal;
